import { useEffect, useId, useMemo, useRef, useState } from "react";

const COLUMN_GROUPS = ["source", "vector", "verdict"];
const NODE_WIDTH = 14;
const NODE_PADDING = 16;
const LABEL_GAP = 8;

/**
 * Three column sankey (source -> vector -> verdict) drawn as plain SVG.
 * nodes: [{ name, group }], links: [{ source, target, value }] with names as ids.
 */
const SankeyChart = ({ nodes, links, colors, height = 300, unitOf = () => "emails" }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [hovered, setHovered] = useState(null);
  // useId returns ":r0:", which is not safe inside url(#...)
  const gradientPrefix = `mgw-sankey-${useId().replace(/:/g, "")}`;

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const layout = useMemo(() => {
    if (!width || !nodes.length) return null;

    const columns = COLUMN_GROUPS.map((group) => nodes.filter((node) => node.group === group));
    const inValue = {};
    const outValue = {};
    links.forEach(({ source, target, value }) => {
      outValue[source] = (outValue[source] || 0) + value;
      inValue[target] = (inValue[target] || 0) + value;
    });
    const valueOf = (name) => Math.max(inValue[name] || 0, outValue[name] || 0);

    // One scale for every column, so a band keeps its thickness from end to end
    const scale = Math.min(
      ...columns
        .filter((column) => column.length)
        .map((column) => {
          const total = column.reduce((sum, node) => sum + valueOf(node.name), 0) || 1;
          return (height - NODE_PADDING * (column.length - 1)) / total;
        }),
    );

    const innerWidth = width - NODE_WIDTH;
    const positioned = {};
    columns.forEach((column, columnIndex) => {
      const columnHeight =
        column.reduce((sum, node) => sum + valueOf(node.name) * scale, 0) +
        NODE_PADDING * (column.length - 1);
      let y = (height - columnHeight) / 2;
      const x = columnIndex === 0 ? 0 : columnIndex === 1 ? innerWidth * 0.45 : innerWidth;
      column.forEach((node) => {
        const nodeHeight = Math.max(valueOf(node.name) * scale, 2);
        positioned[node.name] = {
          ...node,
          x,
          y,
          height: nodeHeight,
          value: valueOf(node.name),
          column: columnIndex,
          outOffset: 0,
          inOffset: 0,
        };
        y += nodeHeight + NODE_PADDING;
      });
    });

    const order = (name) => nodes.findIndex((node) => node.name === name);
    const bands = [...links]
      .filter(({ source, target }) => positioned[source] && positioned[target])
      .sort((a, b) => order(a.source) - order(b.source) || order(a.target) - order(b.target))
      .map((link) => {
        const source = positioned[link.source];
        const target = positioned[link.target];
        const thickness = link.value * scale;
        const sy = source.y + source.outOffset;
        source.outOffset += thickness;
        return { ...link, thickness, sy, sourceNode: source, targetNode: target };
      });

    // Incoming bands are stacked in source order, separately from the outgoing ones
    [...bands]
      .sort((a, b) => order(a.target) - order(b.target) || order(a.source) - order(b.source))
      .forEach((band) => {
        band.ty = band.targetNode.y + band.targetNode.inOffset;
        band.targetNode.inOffset += band.thickness;
      });

    return { positioned: Object.values(positioned), bands };
  }, [width, nodes, links, height]);

  const isActive = (band) => !hovered || band.source === hovered || band.target === hovered;

  const bandPath = ({ sourceNode, targetNode, sy, ty, thickness }) => {
    const x0 = sourceNode.x + NODE_WIDTH;
    const x1 = targetNode.x;
    const xm = (x0 + x1) / 2;
    return `M${x0},${sy} C${xm},${sy} ${xm},${ty} ${x1},${ty} L${x1},${ty + thickness} C${xm},${ty + thickness} ${xm},${sy + thickness} ${x0},${sy + thickness} Z`;
  };

  return (
    <div ref={containerRef} className="mgw-sankey">
      {layout && (
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Mail flow sankey"
        >
          <defs>
            {/* Each band fades from its source node's colour to its target node's */}
            {layout.bands.map((band, index) => (
              <linearGradient
                key={`${band.source}-${band.target}`}
                id={`${gradientPrefix}-${index}`}
                gradientUnits="userSpaceOnUse"
                x1={band.sourceNode.x + NODE_WIDTH}
                x2={band.targetNode.x}
                y1={0}
                y2={0}
              >
                <stop offset="0%" stopColor={colors[band.source]} />
                <stop offset="100%" stopColor={colors[band.target]} />
              </linearGradient>
            ))}
          </defs>
          {layout.bands.map((band, index) => (
            <path
              key={`${band.source}-${band.target}`}
              d={bandPath(band)}
              fill={`url(#${gradientPrefix}-${index})`}
              className={`mgw-sankey__band ${isActive(band) ? "" : "mgw-sankey__band--dim"}`}
            >
              <title>{`${band.source} → ${band.target}: ${band.value} ${unitOf(band)}`}</title>
            </path>
          ))}
          {layout.positioned.map((node) => (
            <g
              key={node.name}
              onMouseEnter={() => setHovered(node.name)}
              onMouseLeave={() => setHovered(null)}
              className="mgw-sankey__node"
            >
              <rect
                x={node.x}
                y={node.y}
                width={NODE_WIDTH}
                height={node.height}
                rx={3}
                fill={colors[node.name]}
              />
              {/* Labels sit over the bands: right of a node, left of the last column's */}
              <text
                x={
                  node.column === COLUMN_GROUPS.length - 1
                    ? node.x - LABEL_GAP
                    : node.x + NODE_WIDTH + LABEL_GAP
                }
                y={node.y + node.height / 2}
                dy="0.35em"
                textAnchor={node.column === COLUMN_GROUPS.length - 1 ? "end" : "start"}
                className="mgw-sankey__label"
              >
                {node.name}
                <tspan className="mgw-sankey__value"> {node.value.toLocaleString()}</tspan>
              </text>
              <title>{`${node.name}: ${node.value}`}</title>
            </g>
          ))}
        </svg>
      )}
    </div>
  );
};

export default SankeyChart;
